import { FormProvider } from 'react-hook-form';
import { ModalV2 } from '@components/modals/modal-v2/modal-v2';
import { createMultiInviteTextRows as createBulkProcessTextRows } from '@components/process-invite-guest/utils/create-multi-invite-text-rows';
import { IMPORT_TYPE_PASS_HOLDER } from '@constants/import-process';
import { useBulkImportPassHolders } from '@hooks/use-bulk-import-pass-holders/use-bulk-import-pass-holders';
import { useBulkProcessNavigation } from '@hooks/use-bulk-process-navigation';
import { useProcessForm } from '@hooks/use-process-form/use-process-form';
import { ProcessFormFields, UseProcessFormReturn } from '@hooks/use-process-form/use-process-form.type';

import { AddBulkHolderModalProps, BulkHolderFormData, ProcessAddBulkHolderStep } from './add-bulk-holder-modal.type';
import { BulkHolderSteps } from './bulk-holder-steps';

export const AddBulkHolderModal = ({
  isOpen,
  onClose,
  onSelectSingle,
  passId,
  isNominative = true,
  passOptions,
  canBulkImport,
  isNominativePass,
}: AddBulkHolderModalProps) => {
  const processForm: UseProcessFormReturn = useProcessForm({
    additionalDefaults: {
      bookingFormFields: {},
      passId,
    },
    validationMode: 'manual',
  });

  const { methods, step, setStep, closeModal, eventId, triggerValidation } = processForm;
  const formPassId = (methods.watch('passId') as string | undefined) ?? passId;
  const isFormPassNominative = isNominativePass?.(formPassId) ?? isNominative;

  const { handleSubmit } = methods;

  const {
    data: bulkImportData,
    mutate: bulkImportPassHolders,
    isSuccess: bulkImportSuccess,
    isPending: isBulkImportLoading,
  } = useBulkImportPassHolders({ hideToast: true });

  // Use the shared import navigation hook for consistent bulk process handling
  useBulkProcessNavigation({
    eventId,
    importType: IMPORT_TYPE_PASS_HOLDER,
    importSuccess: bulkImportSuccess,
    importData: bulkImportData,
  });

  // Create a wrapper function to match the expected interface
  const handleTriggerValidation = async (fieldName?: string): Promise<boolean> => {
    if (fieldName) {
      return await triggerValidation(fieldName as keyof ProcessFormFields);
    }
    return await triggerValidation();
  };

  const handleBulkHolderSubmit = (formData: BulkHolderFormData) => {
    if (formData.bulkHolderText) {
      const parsedRows = createBulkProcessTextRows(formData.bulkHolderText);
      const rows = parsedRows.map((parsedRow) => {
        const baseRow = {
          row: parsedRow.row,
          email: parsedRow.email ?? '',
          fullName: parsedRow.fullName,
        };

        const numTickets =
          typeof parsedRow.numTickets === 'string' ? Number.parseInt(parsedRow.numTickets, 10) : parsedRow.numTickets;

        return isFormPassNominative ? baseRow : { ...baseRow, numTickets };
      });

      const importData = {
        channelId: formData.channelId,
        rows,
      };

      bulkImportPassHolders({ passId: formData.passId, importData });
    }
  };

  const onSubmit = (formData: ProcessFormFields & { bulkHolderText?: string }) => {
    handleBulkHolderSubmit({
      passId: formData.passId ?? passId,
      channelId: formData.channelId,
      bulkHolderText: formData.bulkHolderText ?? '',
    });
  };

  // Success handling and navigation is now managed by useBulkProcessNavigation hook

  // Close modal handler
  const handleClose = () => {
    closeModal();
    onClose();
  };

  return (
    <ModalV2 isOpen={isOpen} onClose={handleClose} variant='md'>
      <form className='flex justify-end flex-col relative'>
        <FormProvider {...methods}>
          <BulkHolderSteps
            step={step as ProcessAddBulkHolderStep}
            setStep={setStep}
            passId={formPassId}
            isLoading={isBulkImportLoading}
            onSubmit={handleSubmit(onSubmit)}
            onClose={handleClose}
            onSelectSingle={onSelectSingle}
            isNominative={isFormPassNominative}
            triggerValidation={handleTriggerValidation}
            passOptions={passOptions}
            canBulkImport={canBulkImport}
          />
        </FormProvider>
      </form>
    </ModalV2>
  );
};
